import { applyCommand, invertCommand, type Command } from "./commands.ts";
import type { WorldDocument } from "./types.ts";

/**
 * Undo and redo over editor commands. History is plain data: commands already applied, newest last, and
 * commands undone and available to redo, newest last. Executing a new command discards the redo stack.
 */
export interface History {
  readonly past: readonly Command[];
  readonly future: readonly Command[];
}

/**
 * A document together with its history, the unit the editor holds and replaces on every edit.
 */
export interface EditorState {
  readonly document: WorldDocument;
  readonly history: History;
}

/**
 * History with nothing to undo or redo.
 */
export const EMPTY_HISTORY: History = { past: [], future: [] };

/**
 * Most commands kept for undo; the oldest is dropped when a new command would exceed it.
 */
export const HISTORY_CAP = 200;

/**
 * Reports whether an undo is available.
 *
 * @param history History to inspect.
 * @returns True when at least one command can be undone.
 */
export function canUndo(history: History): boolean {
  return history.past.length > 0;
}

/**
 * Reports whether a redo is available.
 *
 * @param history History to inspect.
 * @returns True when at least one undone command can be reapplied.
 */
export function canRedo(history: History): boolean {
  return history.future.length > 0;
}

/**
 * Applies a command and records it. The redo stack is cleared because the new command branches history, and
 * the oldest command is dropped once `HISTORY_CAP` is reached.
 *
 * @param state Current document and history; never mutated.
 * @param command Command to apply.
 * @returns The new state.
 * @throws Error when the command cannot be applied; the input state is unchanged.
 */
export function execute(state: EditorState, command: Command): EditorState {
  const document = applyCommand(state.document, command);
  const past = state.history.past.length >= HISTORY_CAP ? [...state.history.past.slice(state.history.past.length - HISTORY_CAP + 1), command] : [...state.history.past, command];
  return { document, history: { past, future: [] } };
}

/**
 * Reverts the most recent command.
 *
 * @param state Current document and history.
 * @returns The new state, or the same state when there is nothing to undo.
 * @throws Error when the recorded command no longer matches the document, which indicates the document was
 *   changed outside the history.
 */
export function undo(state: EditorState): EditorState {
  const command = state.history.past[state.history.past.length - 1];
  if (command === undefined) {
    return state;
  }
  const document = applyCommand(state.document, invertCommand(command));
  return { document, history: { past: state.history.past.slice(0, -1), future: [...state.history.future, command] } };
}

/**
 * Reapplies the most recently undone command.
 *
 * @param state Current document and history.
 * @returns The new state, or the same state when there is nothing to redo.
 * @throws Error when the recorded command no longer matches the document.
 */
export function redo(state: EditorState): EditorState {
  const command = state.history.future[state.history.future.length - 1];
  if (command === undefined) {
    return state;
  }
  const document = applyCommand(state.document, command);
  return { document, history: { past: [...state.history.past, command], future: state.history.future.slice(0, -1) } };
}
